import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

// Context Providers
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";

// Components
import LandingPage from "@/components/landing/LandingPage";
import ContactPage from "@/components/landing/ContactPage";
import ReschedulePage from "@/components/landing/ReschedulePage";
import CancelPage from "@/components/landing/CancelPage";
import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import AcceptInvitePage from "@/components/auth/AcceptInvitePage";
import ForgotPasswordPage from "@/components/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/components/auth/ResetPasswordPage";
import LinkedInCallback from "@/components/auth/LinkedInCallback";
import TwitterCallback from "@/components/auth/TwitterCallback";
import FacebookCallback from "@/components/auth/FacebookCallback";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import AdminLayout from "@/components/layouts/AdminLayout";
import PublicSharePage from "@/components/public/PublicSharePage";
import PublicEmbedWidget from "@/components/public/PublicEmbedWidget";
import PublicAnalyticsSnapshotPage from "@/components/public/PublicAnalyticsSnapshotPage";
import TrustExplainerPage from "@/components/public/TrustExplainerPage";
import UnsubscribedPage from "@/components/UnsubscribedPage";
import VerifyEmailPage from "@/components/auth/VerifyEmailPage";
import ActivatePage from "@/components/auth/ActivatePage";

// Suppress benign ResizeObserver loop errors that Radix Select bubbles up
// AND generic cross-origin "Script error." events (fired by third-party
// scripts like Cloudflare Turnstile when their bundle errors — real users
// aren't affected but the dev overlay was blocking preview UX).
// `capture: true` ensures we run BEFORE React's dev-overlay listener.
if (typeof window !== 'undefined') {
  // iter175 follow-up — Also unmount the CRA react-error-overlay iframe if it
  // slips through and paints itself over the app. The overlay is dev-only
  // (production builds don't ship react-error-overlay) so this is a preview
  // convenience, not a prod change.
  const stripReactErrorOverlay = () => {
    try {
      const iframes = document.querySelectorAll('body > iframe');
      iframes.forEach((frame) => {
        const title = (frame.getAttribute('title') || '').toLowerCase();
        const id = (frame.id || '').toLowerCase();
        if (
          title.includes('runtime error') ||
          title.includes('error overlay') ||
          id.includes('webpack-dev-server-client-overlay') ||
          id.includes('error-overlay')
        ) {
          frame.remove();
        }
      });
    } catch { /* ignore DOM race */ }
  };

  const ro = (e) => {
    const msg = e?.message || '';
    if (msg.startsWith?.('ResizeObserver loop') || msg === 'Script error.' || msg === 'Script error') {
      e.stopImmediatePropagation();
      e.preventDefault();
      // The overlay may already have mounted (its listener ran before ours in
      // some capture-phase orderings); strip it on the next tick so preview
      // isn't visually blocked by a benign cross-origin "Script error.".
      setTimeout(stripReactErrorOverlay, 0);
    }
  };
  window.addEventListener('error', ro, true);
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason?.message || String(e?.reason || '');
    if (reason.startsWith('Script error')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      setTimeout(stripReactErrorOverlay, 0);
    }
  }, true);

  // Also observe for late-mounted overlay iframes (they can appear after the
  // initial error swallow if a second Script error fires) and strip them.
  if (typeof MutationObserver !== 'undefined') {
    const start = () => {
      if (!document.body) return;
      const mo = new MutationObserver(() => stripReactErrorOverlay());
      mo.observe(document.body, { childList: true, subtree: false });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
        <FeatureFlagsProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/book-a-demo" element={<LandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reschedule/:token" element={<ReschedulePage />} />
              <Route path="/cancel/:token" element={<CancelPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* iter149 — Team-invite one-click accept landing page (from SendGrid email) */}
              <Route path="/invite" element={<AcceptInvitePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
              <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
              <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
              <Route path="/share/:token" element={<PublicSharePage />} />
              <Route path="/embed/:token" element={<PublicEmbedWidget />} />
              <Route path="/analytics-snapshot/:token" element={<PublicAnalyticsSnapshotPage />} />
              {/* iter178 — Trust Badge explainer / marketing page. */}
              <Route path="/trust" element={<TrustExplainerPage />} />
              <Route path="/unsubscribed" element={<UnsubscribedPage />} />
              {/* iter175 — Email-verification landing (backend redirects here after decoding the token). */}
              <Route path="/verify-email/:status" element={<VerifyEmailPage />} />
              {/* iter250 — Activation screen: where a blocked sign-in or an
                  email-less OAuth signup lands to get its link. */}
              <Route path="/activate" element={<ActivatePage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard/*" element={<DashboardLayout />} />
              <Route path="/admin/*" element={<AdminLayout />} />
            </Routes>
          </BrowserRouter>
          
          {/* iter87 — Cap sonner stacking at 3 visible toasts and disable
              expansion. Combined with stable `id` params on all recurrent
              toast calls (notification bell, economist load errors, etc.),
              this eliminates the "toast wall" the user saw when the same
              404 fired twice from a repeated deep-link open. */}
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            offset={80}
            visibleToasts={3}
            expand={false}
            toastOptions={{
              duration: 4000,
            }}
          />
        </div>
        </FeatureFlagsProvider>
      </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
